import { useState } from 'react'
import api from '../../lib/api'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { oneDark, oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism'
import {
  Play,
  Copy,
  CheckCircle,
  XCircle,
  RefreshCw,
  Moon,
  Sun,
  Search,
  ChevronDown,
  ChevronRight,
  Code,
  Book,
  Shield,
  Smartphone,
  MessageSquare,
  MessageCircle,
  Zap,
  Database,
  Settings,
  Menu,
  X,
  Eye,
  EyeOff,
  Key,
  Send,
  AlertCircle,
  Loader2
} from 'lucide-react'

interface Parameter {
  name: string
  type: string
  required: boolean
  description: string
  example?: string
  schema?: any
}

interface Response {
  status: number
  description: string
  headers?: Record<string, string>
  body?: any
  example?: string
}

interface Endpoint {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  path: string
  summary: string
  description: string
  tags: string[]
  parameters?: Parameter[]
  requestBody?: {
    contentType: string
    schema: any
    example: string
  }
  responses: Response[]
  examples?: Record<string, string>
  disableTryItOut?: boolean
}

interface ApiTestResult {
  status: number
  statusText: string
  headers: Record<string, string>
  data: any
  duration: number
  error?: string
}

interface TryItOutState {
  apiKey: string
  parameters: Record<string, any>
  requestBody: string
  isLoading: boolean
  result: ApiTestResult | null
  error: string | null
  showCredentials: boolean
}

interface ApiCategory {
  name: string
  icon: React.ReactNode
  endpoints: Endpoint[]
}

const methodColors = {
  GET: 'bg-emerald-600 text-white border-emerald-700',
  POST: 'bg-blue-600 text-white border-blue-700',
  PUT: 'bg-amber-600 text-white border-amber-700',
  DELETE: 'bg-red-600 text-white border-red-700',
  PATCH: 'bg-purple-600 text-white border-purple-700'
}

const methodColorsDark = {
  GET: 'bg-emerald-600 text-white border-emerald-700',
  POST: 'bg-blue-600 text-white border-blue-700',
  PUT: 'bg-amber-600 text-white border-amber-700',
  DELETE: 'bg-red-600 text-white border-red-700',
  PATCH: 'bg-purple-600 text-white border-purple-700'
}

const apiCategories: ApiCategory[] = [
  {
    name: 'SMS',
    icon: <Smartphone className="w-4 h-4" />,
    endpoints: [
      {
        method: 'POST',
        path: '/v1/sms/send',
        summary: 'Send SMS',
        description: 'Send a single SMS message to a phone number',
        tags: ['SMS'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['to', 'message'],
            properties: {
              to: { type: 'string', pattern: '^\\+[1-9]\\d{6,14}$' },
              message: { type: 'string', maxLength: 1600 },
              deviceId: { type: 'string' }
            }
          },
          example: `{
  "to": "+1234567890",
  "message": "Hello from redgeGate!",
  "deviceId": "60d5ec49f1b2c8a1e8b4567a"
}`
        },
        parameters: [],
        responses: [
          {
            status: 200,
            description: 'SMS queued successfully',
            body: {
              success: true,
              data: {
                smsId: '68e37285c83ac140f2485d20',
                status: 'queued',
                recipient: '+1234567890',
                message: 'Hello from redgeGate!',
                deviceId: '60d5ec49f1b2c8a1e8b4567a',
                deviceInfo: {
                  brand: 'Samsung',
                  model: 'Galaxy S21',
                  os: 'Android',
                  osVersion: '11'
                },
                createdAt: '2024-01-15T10:30:00.000Z',
                cost: { amount: 0.05, currency: 'USD' }
              }
            }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/v1/sms/send' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "to": "+1234567890",
    "message": "Hello from redgeGate!"
  }'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/sms/send', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '+1234567890',
    message: 'Hello from redgeGate!'
  })
});

const data = await response.json();
console.log(data);`,
          python: `import requests

url = 'https://text.teamredge.com/api/v1/sms/send'
headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
}
payload = {
    'to': '+1234567890',
    'message': 'Hello from redgeGate!'
}

response = requests.post(url, headers=headers, json=payload)
print(response.json())`
        }
      },
      {
        method: 'POST',
        path: '/v1/sms/send-bulk',
        summary: 'Send Bulk SMS',
        description: 'Send SMS messages to multiple recipients',
        tags: ['SMS'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['messages'],
            properties: {
              deviceId: { type: 'string' },
              messages: {
                type: 'array',
                items: {
                  type: 'object',
                  required: ['to', 'message'],
                  properties: {
                    to: { type: 'string' },
                    message: { type: 'string' }
                  }
                }
              }
            }
          },
          example: `{
  "deviceId": "60d5ec49f1b2c8a1e8b4567a",
  "messages": [
    {
      "to": "+1234567890",
      "message": "Hello John!"
    },
    {
      "to": "+0987654321",
      "message": "Hello Jane!"
    }
  ]
}`
        },
        parameters: [],
        responses: [
          {
            status: 200,
            description: 'Bulk SMS queued successfully',
            body: {
              success: true,
              data: {
                batchId: 'batch_1a2b3c4d5e6f',
                status: 'queued',
                recipientCount: 2,
                messages: [
                  { smsId: '68e37285c83ac140f2485d20', recipient: '+1234567890', status: 'queued' },
                  { smsId: 'msg_2b3c4d5e6f7a', recipient: '+0987654321', status: 'queued' }
                ],
                totalCost: { amount: 0.10, currency: 'USD' }
              }
            }
          }
        ]
      },
      {
        method: 'GET',
        path: '/v1/messages',
        summary: 'Get Messages',
        description: 'Retrieve message history with pagination',
        tags: ['SMS'],
        parameters: [
          {
            name: 'page',
            type: 'integer',
            required: false,
            description: 'Page number for pagination',
            example: '1'
          },
          {
            name: 'limit',
            type: 'integer',
            required: false,
            description: 'Number of messages per page',
            example: '20'
          },
          {
            name: 'status',
            type: 'string',
            required: false,
            description: 'Filter by message status',
            example: 'delivered'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Message history retrieved',
            body: {
              success: true,
              data: {
                messages: [
                  {
                    smsId: '68e37285c83ac140f2485d20',
                    recipient: '+1234567890',
                    message: 'Hello from redgeGate!',
                    channel: 'sms',
                    status: 'delivered',
                    direction: 'outbound',
                    createdAt: '2024-01-15T10:30:00.000Z',
                    sentAt: '2024-01-15T10:30:10.000Z',
                    deliveredAt: '2024-01-15T10:30:25.000Z',
                    readAt: null
                  }
                ],
                pagination: {
                  page: 1,
                  limit: 20,
                  total: 100,
                  pages: 5,
                  hasNext: true,
                  hasPrev: false
                }
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/messages?page=1&limit=20&status=delivered' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/messages?page=1&limit=20&status=delivered', {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.messages);`,
          python: `import requests

headers = {'X-API-Key': 'YOUR_API_KEY'}
params = {'page': 1, 'limit': 20, 'status': 'delivered'}

response = requests.get('https://text.teamredge.com/api/v1/messages', headers=headers, params=params)
data = response.json()
print(data['data']['messages'])`
        }
      },
      {
        method: 'GET',
        path: '/v1/messages/{smsId}',
        summary: 'Get Message Details',
        description: 'Get detailed information about a specific SMS message. The API validates ownership and only returns SMS details if the message belongs to the authenticated user.',
        tags: ['SMS'],
        parameters: [
          {
            name: 'smsId',
            type: 'string',
            required: true,
            description: 'ID of the SMS message',
            example: '68e37285c83ac140f2485d20'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Message details retrieved',
            body: {
              success: true,
              data: {
                message: {
                  smsId: '68e37285c83ac140f2485d20',
                  recipient: '+1234567890',
                  message: 'Hello from redgeGate!',
                  channel: 'sms',
                  status: 'read',
                  device: {
                    id: '60d5ec49f1b2c8a1e8b4567a',
                    name: 'Samsung Galaxy S21'
                  },
                  createdAt: '2024-01-15T10:30:00.000Z',
                  sentAt: '2024-01-15T10:30:10.000Z',
                  deliveredAt: '2024-01-15T10:30:25.000Z',
                  readAt: '2024-01-15T10:31:05.000Z',
                  updatedAt: '2024-01-15T10:31:05.000Z'
                }
              }
            }
          },
          {
            status: 404,
            description: 'Message not found',
            body: {
              success: false,
              error: {
                code: 'MESSAGE_NOT_FOUND',
                message: 'The specified message ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/messages/68e37285c83ac140f2485d20' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const smsId = '68e37285c83ac140f2485d20';
const response = await fetch(\`https://text.teamredge.com/api/v1/messages/\${smsId}\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.message);`,
          python: `import requests

sms_id = '68e37285c83ac140f2485d20'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get(f'https://text.teamredge.com/api/v1/messages/{sms_id}', headers=headers)
data = response.json()
print(data['data']['message'])`
        }
      }
    ]
  },
  {
    name: 'Batch Management',
    icon: <RefreshCw className="w-4 h-4" />,
    endpoints: [
      {
        method: 'POST',
        path: '/queue/batch/{batchId}/pause',
        summary: 'Pause Batch',
        description: 'Gracefully pause an active bulk SMS batch processing. The system will wait 2 seconds for current jobs to complete before halting further processing.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'batchId',
            type: 'string',
            required: true,
            description: 'ID of the SMS batch to pause',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            properties: {
              reason: { type: 'string', maxLength: 500 }
            }
          },
          example: `{
  "reason": "Maintenance window - will resume after 1 hour"
}`
        },
        responses: [
          {
            status: 200,
            description: 'Batch pause initiated successfully',
            body: {
              success: true,
              data: {
                batchId: 'batch_1a2b3c4d5e6f',
                status: 'pausing',
                pausedAt: '2024-01-15T10:30:00.000Z',
                pausingAt: '2024-01-15T10:30:00.000Z',
                pauseReason: 'Maintenance window - will resume after 1 hour',
                processingProgress: {
                  completedCount: 15,
                  failedCount: 2,
                  totalCount: 50,
                  successCount: 13,
                  deliveredCount: 10
                },
                message: 'Batch pause initiated. Current jobs will complete before halting.'
              }
            }
          },
          {
            status: 400,
            description: 'Cannot pause batch',
            body: {
              success: false,
              error: {
                code: 'CANNOT_PAUSE_BATCH',
                message: 'Cannot pause batch that is completed'
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/queue/batch/batch_1a2b3c4d5e6f/pause' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "reason": "Maintenance window"
  }'`,
          javascript: `const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/queue/batch/\${batchId}/pause\`, {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    reason: 'Maintenance window'
  })
});

const data = await response.json();
console.log(data);`,
          python: `import requests

batch_id = 'batch_1a2b3c4d5e6f'
headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
}
payload = {
    'reason': 'Maintenance window'
}

response = requests.post(f'https://text.teamredge.com/api/queue/batch/{batch_id}/pause', headers=headers, json=payload)
print(response.json())`
        }
      },
      {
        method: 'POST',
        path: '/queue/batch/{batchId}/resume',
        summary: 'Resume Batch',
        description: 'Resume a paused bulk SMS batch. The system will calculate remaining SMS to send and auto-complete the batch if no pending SMS remain.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'batchId',
            type: 'string',
            required: true,
            description: 'ID of the SMS batch to resume',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            properties: {}
          },
          example: '{}'
        },
        responses: [
          {
            status: 200,
            description: 'Batch resumed successfully',
            body: {
              success: true,
              data: {
                batchId: 'batch_1a2b3c4d5e6f',
                status: 'resuming',
                resumedAt: '2024-01-15T11:30:00.000Z',
                pausedAt: '2024-01-15T10:30:00.000Z',
                totalPauseDuration: 3600000,
                remainingSMSCount: 35,
                processingProgress: {
                  completedCount: 15,
                  failedCount: 2,
                  totalCount: 50,
                  successCount: 13,
                  deliveredCount: 10
                },
                message: 'Batch resumed successfully. Remaining SMS will be processed.'
              }
            }
          },
          {
            status: 400,
            description: 'Cannot resume batch',
            body: {
              success: false,
              error: {
                code: 'CANNOT_RESUME_BATCH',
                message: 'Cannot resume batch in completed status. Only paused batches can be resumed.'
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/queue/batch/batch_1a2b3c4d5e6f/resume' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json'`,
          javascript: `const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/queue/batch/\${batchId}/resume\`, {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`,
          python: `import requests

batch_id = 'batch_1a2b3c4d5e6f'
headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
}

response = requests.post(f'https://text.teamredge.com/api/queue/batch/{batch_id}/resume', headers=headers)
print(response.json())`
        }
      },
      {
        method: 'GET',
        path: '/queue/batch/{batchId}/pause-history',
        summary: 'Get Batch Pause History',
        description: 'Get complete audit trail of all pause/resume actions for a specific batch with user attribution and processing progress.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'batchId',
            type: 'string',
            required: true,
            description: 'ID of the SMS batch',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Pause history retrieved successfully',
            body: {
              success: true,
              data: {
                batchId: 'batch_1a2b3c4d5e6f',
                pauseHistory: [
                  {
                    id: 'pause_log_1',
                    action: 'paused',
                    reason: 'Maintenance window',
                    timestamp: '2024-01-15T10:30:00.000Z',
                    user: {
                      id: 'user_123',
                      email: 'admin@example.com'
                    },
                    processingProgress: {
                      completedCount: 15,
                      failedCount: 2,
                      totalCount: 50,
                      successCount: 13,
                      deliveredCount: 10
                    },
                    metadata: {
                      userAgent: 'Mozilla/5.0...',
                      ip: '192.168.1.100'
                    }
                  },
                  {
                    id: 'pause_log_2',
                    action: 'resumed',
                    timestamp: '2024-01-15T11:30:00.000Z',
                    user: {
                      id: 'user_123',
                      email: 'admin@example.com'
                    },
                    processingProgress: {
                      completedCount: 15,
                      failedCount: 2,
                      totalCount: 50,
                      successCount: 13,
                      deliveredCount: 10
                    },
                    metadata: {
                      userAgent: 'Mozilla/5.0...',
                      ip: '192.168.1.100'
                    }
                  }
                ],
                totalPauseDuration: 3600000,
                pauseCount: 1
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/queue/batch/batch_1a2b3c4d5e6f/pause-history' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/queue/batch/\${batchId}/pause-history\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.pauseHistory);`,
          python: `import requests

batch_id = 'batch_1a2b3c4d5e6f'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get(f'https://text.teamredge.com/api/queue/batch/{batch_id}/pause-history', headers=headers)
data = response.json()
print(data['data']['pauseHistory'])`
        }
      },
      {
        method: 'GET',
        path: '/gateway/devices/{deviceId}/sms-batch/{smsBatchId}',
        summary: 'Get SMS Batch Details',
        description: 'Get detailed information about a specific SMS batch including pause/resume status and processing progress.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'deviceId',
            type: 'string',
            required: true,
            description: 'ID of the device',
            example: '60d5ec49f1b2c8a1e8b4567a'
          },
          {
            name: 'smsBatchId',
            type: 'string',
            required: true,
            description: 'ID of the SMS batch',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'SMS batch details retrieved',
            body: {
              success: true,
              data: {
                batch: {
                  id: 'batch_1a2b3c4d5e6f',
                  device: '60d5ec49f1b2c8a1e8b4567a',
                  message: 'Hello to all recipients!',
                  recipientCount: 50,
                  recipientPreview: '+1234567890, +0987654321, +1111111111...',
                  status: 'paused',
                  pausedAt: '2024-01-15T10:30:00.000Z',
                  resumedAt: null,
                  pausingAt: '2024-01-15T10:29:58.000Z',
                  pauseReason: 'Maintenance window',
                  pauseCount: 1,
                  totalPauseDuration: 0,
                  createdAt: '2024-01-15T10:25:00.000Z',
                  completedAt: null,
                  processingProgress: {
                    completedCount: 15,
                    failedCount: 2,
                    totalCount: 50,
                    successCount: 13,
                    deliveredCount: 10,
                    lastProcessedIndex: 16
                  }
                }
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/gateway/devices/60d5ec49f1b2c8a1e8b4567a/sms-batch/batch_1a2b3c4d5e6f' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const deviceId = '60d5ec49f1b2c8a1e8b4567a';
const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/gateway/devices/\${deviceId}/sms-batch/\${batchId}\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.batch);`,
          python: `import requests

device_id = '60d5ec49f1b2c8a1e8b4567a'
batch_id = 'batch_1a2b3c4d5e6f'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get(f'https://text.teamredge.com/api/gateway/devices/{device_id}/sms-batch/{batch_id}', headers=headers)
data = response.json()
print(data['data']['batch'])`
        }
      },
      {
        method: 'POST',
        path: '/queue/batch/{batchId}/cancel',
        summary: 'Cancel Batch',
        description: 'Cancel a pending or processing bulk SMS batch. All remaining SMS will be marked as cancelled and will not be sent.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'batchId',
            type: 'string',
            required: true,
            description: 'ID of the SMS batch to cancel',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            properties: {}
          },
          example: '{}'
        },
        responses: [
          {
            status: 200,
            description: 'Batch cancelled successfully',
            body: {
              success: true,
              message: 'Batch cancelled successfully',
              data: {
                batchId: 'batch_1a2b3c4d5e6f',
                cancelledAt: '2024-01-15T10:30:00.000Z',
                affectedSMSCount: 50
              }
            }
          },
          {
            status: 400,
            description: 'Cannot cancel batch',
            body: {
              success: false,
              error: {
                code: 'CANNOT_CANCEL_BATCH',
                message: 'Only pending or processing batches can be cancelled'
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/queue/batch/batch_1a2b3c4d5e6f/cancel' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json'`,
          javascript: `const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/queue/batch/\${batchId}/cancel\`, {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`,
          python: `import requests

batch_id = 'batch_1a2b3c4d5e6f'
headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
}

response = requests.post(f'https://text.teamredge.com/api/queue/batch/{batch_id}/cancel', headers=headers)
print(response.json())`
        }
      },
      {
        method: 'GET',
        path: '/queue/batch/{batchId}',
        summary: 'Get Batch Details',
        description: 'Get detailed information about a specific SMS batch including all SMS messages in the batch with their individual status.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'batchId',
            type: 'string',
            required: true,
            description: 'ID of the SMS batch',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Batch details retrieved successfully',
            body: {
              success: true,
              data: {
                batch: {
                  _id: 'batch_1a2b3c4d5e6f',
                  device: {
                    _id: '60d5ec49f1b2c8a1e8b4567a',
                    brand: 'Samsung',
                    model: 'Galaxy S21',
                    enabled: true
                  },
                  message: 'Hello to all recipients!',
                  recipientCount: 50,
                  recipientPreview: '+1234567890, +0987654321, +1111111111...',
                  status: 'completed',
                  successCount: 45,
                  failureCount: 5,
                  deliveredCount: 42,
                  createdAt: '2024-01-15T10:25:00.000Z',
                  completedAt: '2024-01-15T10:45:00.000Z'
                },
                sms: [
                  {
                    recipient: '+1234567890',
                    status: 'delivered',
                    sentAt: '2024-01-15T10:30:00.000Z',
                    deliveredAt: '2024-01-15T10:30:25.000Z',
                    failedAt: null,
                    errorMessage: null
                  },
                  {
                    recipient: '+0987654321',
                    status: 'failed',
                    sentAt: '2024-01-15T10:31:00.000Z',
                    deliveredAt: null,
                    failedAt: '2024-01-15T10:31:30.000Z',
                    errorMessage: 'Device not responding'
                  }
                ]
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/queue/batch/batch_1a2b3c4d5e6f' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/queue/batch/\${batchId}\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.batch, data.data.sms);`,
          python: `import requests

batch_id = 'batch_1a2b3c4d5e6f'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get(f'https://text.teamredge.com/api/queue/batch/{batch_id}', headers=headers)
data = response.json()
print(data['data']['batch'])
print(data['data']['sms'])`
        }
      },
      {
        method: 'POST',
        path: '/queue/batch/{batchId}/retry',
        summary: 'Retry Failed Batch',
        description: 'Retry failed or partially successful SMS batches. Creates a new batch containing only the failed SMS from the original batch and queues it for processing.',
        tags: ['Batch Management'],
        parameters: [
          {
            name: 'batchId',
            type: 'string',
            required: true,
            description: 'ID of the failed SMS batch to retry',
            example: 'batch_1a2b3c4d5e6f'
          }
        ],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            properties: {}
          },
          example: '{}'
        },
        responses: [
          {
            status: 200,
            description: 'Batch retry queued successfully',
            body: {
              success: true,
              message: 'Batch retry queued successfully',
              data: {
                originalBatchId: 'batch_1a2b3c4d5e6f',
                retryBatchId: 'batch_retry_2b3c4d5e6f7a',
                retryCount: 1,
                failedSMSCount: 5,
                jobId: 'job_3c4d5e6f7a8b'
              }
            }
          },
          {
            status: 400,
            description: 'Cannot retry batch',
            body: {
              success: false,
              error: {
                code: 'CANNOT_RETRY_BATCH',
                message: 'Only failed or partially successful batches can be retried'
              }
            }
          },
          {
            status: 400,
            description: 'No failed SMS',
            body: {
              success: false,
              error: {
                code: 'NO_FAILED_SMS',
                message: 'No failed SMS found in this batch'
              }
            }
          },
          {
            status: 404,
            description: 'Batch not found',
            body: {
              success: false,
              error: {
                code: 'BATCH_NOT_FOUND',
                message: 'The specified batch ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/queue/batch/batch_1a2b3c4d5e6f/retry' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json'`,
          javascript: `const batchId = 'batch_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/queue/batch/\${batchId}/retry\`, {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  }
});

const data = await response.json();
console.log(data);`,
          python: `import requests

batch_id = 'batch_1a2b3c4d5e6f'
headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
}

response = requests.post(f'https://text.teamredge.com/api/queue/batch/{batch_id}/retry', headers=headers)
print(response.json())`
        }
      }
    ]
  },
  {
    name: 'Devices',
    icon: <Database className="w-4 h-4" />,
    endpoints: [
      {
        method: 'GET',
        path: '/v1/devices',
        summary: 'List Devices',
        description: 'Get all registered devices with their status. Returns different fields depending on gateway type: SMS devices include Android hardware info, WhatsApp devices include live session status from OpenWA (phone number, connection state, last active time).',
        tags: ['Devices'],
        parameters: [],
        responses: [
          {
            status: 200,
            description: 'Device list retrieved. Response shape varies by gatewayType.',
            body: {
              status: 'success',
              data: {
                devices: [
                  {
                    id: '60d5ec49f1b2c8a1e8b4567a',
                    enabled: true,
                    gatewayType: 'sms',
                    brand: 'Samsung',
                    manufacturer: 'Samsung',
                    model: 'Galaxy S21',
                    serial: 'R5CN30ABCDE',
                    os: 'Android',
                    osVersion: '13',
                    appVersionName: '2.6.2',
                    appVersionCode: 26,
                    displayName: 'Samsung Galaxy S21',
                    sentSMSCount: 150,
                    receivedSMSCount: 45,
                    messageInterval: 3,
                    createdAt: '2024-01-10T10:30:00.000Z'
                  },
                  {
                    id: '60d5ec49f1b2c8a1e8b4568b',
                    enabled: true,
                    gatewayType: 'whatsapp',
                    displayName: 'My WhatsApp',
                    phone: '628123456789',
                    pushName: 'John Doe',
                    whatsappStatus: 'ready',
                    connectedAt: '2024-01-12T08:00:00.000Z',
                    lastActive: '2024-01-15T10:25:00.000Z',
                    whatsappSessionId: 'wa-abc123-1705000000000',
                    sentSMSCount: 320,
                    receivedSMSCount: 0,
                    messageInterval: 5,
                    createdAt: '2024-01-12T08:00:00.000Z'
                  }
                ]
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/devices' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/devices', {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();

// Filter by gateway type
const smsDevices = data.data.devices.filter(d => d.gatewayType === 'sms');
const waDevices = data.data.devices.filter(d => d.gatewayType === 'whatsapp');

console.log('SMS devices:', smsDevices.length);
console.log('WhatsApp devices:', waDevices.length);`,
          python: `import requests

headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get('https://text.teamredge.com/api/v1/devices', headers=headers)
data = response.json()

for device in data['data']['devices']:
    if device['gatewayType'] == 'whatsapp':
        print(f"WhatsApp: {device['displayName']} ({device.get('phone', 'N/A')}) - {device['whatsappStatus']}")
    else:
        print(f"SMS: {device.get('brand', '')} {device.get('model', '')} - {'enabled' if device['enabled'] else 'disabled'}")`
        }
      },
      {
        method: 'GET',
        path: '/v1/devices/{deviceId}',
        summary: 'Get Device',
        description: 'Get detailed information about a specific device. SMS devices return Android hardware fields (brand, model, os, serial, etc.). WhatsApp devices return live session info from OpenWA (phone, pushName, whatsappStatus, connectedAt, lastActive). If the WhatsApp session is unreachable, whatsappStatus returns "disconnected".',
        tags: ['Devices'],
        parameters: [
          {
            name: 'deviceId',
            type: 'string',
            required: true,
            description: 'ID of the device',
            example: '60d5ec49f1b2c8a1e8b4567a'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Device details retrieved (SMS device example)',
            body: {
              status: 'success',
              data: {
                device: {
                  id: '60d5ec49f1b2c8a1e8b4567a',
                  enabled: true,
                  gatewayType: 'sms',
                  brand: 'Samsung',
                  manufacturer: 'Samsung',
                  model: 'Galaxy S21',
                  serial: 'R5CN30ABCDE',
                  buildId: 'TP1A.220624.014',
                  os: 'Android',
                  osVersion: '13',
                  appVersionName: '2.6.2',
                  appVersionCode: 26,
                  displayName: 'Samsung Galaxy S21',
                  sentSMSCount: 150,
                  receivedSMSCount: 45,
                  messageInterval: 3,
                  createdAt: '2024-01-10T10:30:00.000Z',
                  updatedAt: '2024-01-15T10:25:00.000Z'
                }
              }
            }
          },
          {
            status: 200,
            description: 'Device details retrieved (WhatsApp device example)',
            body: {
              status: 'success',
              data: {
                device: {
                  id: '60d5ec49f1b2c8a1e8b4568b',
                  enabled: true,
                  gatewayType: 'whatsapp',
                  displayName: 'My WhatsApp',
                  phone: '628123456789',
                  pushName: 'John Doe',
                  whatsappStatus: 'ready',
                  connectedAt: '2024-01-12T08:00:00.000Z',
                  lastActive: '2024-01-15T10:25:00.000Z',
                  whatsappSessionId: 'wa-abc123-1705000000000',
                  sentSMSCount: 320,
                  receivedSMSCount: 0,
                  messageInterval: 5,
                  createdAt: '2024-01-12T08:00:00.000Z',
                  updatedAt: '2024-01-15T10:25:00.000Z'
                }
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/devices/60d5ec49f1b2c8a1e8b4567a' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const deviceId = '60d5ec49f1b2c8a1e8b4567a';
const response = await fetch(\`https://text.teamredge.com/api/v1/devices/\${deviceId}\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
const device = data.data.device;

if (device.gatewayType === 'whatsapp') {
  console.log(\`WhatsApp: \${device.displayName} (\${device.phone})\`);
  console.log(\`Status: \${device.whatsappStatus}\`);
  console.log(\`Last active: \${device.lastActive}\`);
} else {
  console.log(\`SMS: \${device.brand} \${device.model}\`);
  console.log(\`OS: \${device.os} \${device.osVersion}\`);
}`,
          python: `import requests

device_id = '60d5ec49f1b2c8a1e8b4567a'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get(f'https://text.teamredge.com/api/v1/devices/{device_id}', headers=headers)
device = response.json()['data']['device']

if device['gatewayType'] == 'whatsapp':
    print(f"WhatsApp: {device['displayName']} ({device.get('phone', 'N/A')})")
    print(f"Status: {device['whatsappStatus']}")
else:
    print(f"SMS: {device.get('brand', '')} {device.get('model', '')}")
    print(f"OS: {device.get('os', '')} {device.get('osVersion', '')}")`
        }
      },
      {
        method: 'GET',
        path: '/v1/devices/{deviceId}/messages',
        summary: 'Get Device Messages',
        description: 'Get message history for a specific device with pagination',
        tags: ['Devices'],
        parameters: [
          {
            name: 'deviceId',
            type: 'string',
            required: true,
            description: 'ID of the device',
            example: '60d5ec49f1b2c8a1e8b4567a'
          },
          {
            name: 'page',
            type: 'integer',
            required: false,
            description: 'Page number for pagination',
            example: '1'
          },
          {
            name: 'limit',
            type: 'integer',
            required: false,
            description: 'Number of messages per page',
            example: '20'
          },
          {
            name: 'status',
            type: 'string',
            required: false,
            description: 'Filter by message status',
            example: 'delivered'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Device message history retrieved',
            body: {
              success: true,
              data: {
                device: {
                  id: '60d5ec49f1b2c8a1e8b4567a',
                  name: 'Samsung Galaxy S21'
                },
                messages: [
                  {
                    smsId: '68e37285c83ac140f2485d20',
                    recipient: '+1234567890',
                    message: 'Hello from redgeGate!',
                    channel: 'sms',
                    status: 'delivered',
                    createdAt: '2024-01-15T10:30:00.000Z',
                    sentAt: '2024-01-15T10:30:10.000Z',
                    deliveredAt: '2024-01-15T10:30:25.000Z',
                    readAt: null
                  }
                ],
                pagination: {
                  page: 1,
                  limit: 20,
                  total: 50,
                  pages: 3,
                  hasNext: true,
                  hasPrev: false
                }
              }
            }
          },
          {
            status: 404,
            description: 'Device not found',
            body: {
              success: false,
              error: {
                code: 'DEVICE_NOT_FOUND',
                message: 'The specified device ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/devices/60d5ec49f1b2c8a1e8b4567a/messages?page=1&limit=20' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const deviceId = '60d5ec49f1b2c8a1e8b4567a';
const response = await fetch(\`https://text.teamredge.com/api/v1/devices/\${deviceId}/messages?page=1&limit=20\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.messages);`,
          python: `import requests

device_id = '60d5ec49f1b2c8a1e8b4567a'
headers = {'X-API-Key': 'YOUR_API_KEY'}
params = {'page': 1, 'limit': 20}

response = requests.get(f'https://text.teamredge.com/api/v1/devices/{device_id}/messages', headers=headers, params=params)
data = response.json()
print(data['data']['messages'])`
        }
      },
      {
        method: 'GET',
        path: '/v1/devices/{deviceId}/sms/{smsId}',
        summary: 'Get Device SMS Details',
        description: 'Get detailed information about a specific SMS message from a specific device',
        tags: ['Devices'],
        parameters: [
          {
            name: 'deviceId',
            type: 'string',
            required: true,
            description: 'ID of the device',
            example: '60d5ec49f1b2c8a1e8b4567a'
          },
          {
            name: 'smsId',
            type: 'string',
            required: true,
            description: 'ID of the SMS message',
            example: 'msg_1a2b3c4d5e6f'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Device SMS details retrieved',
            body: {
              success: true,
              data: {
                device: {
                  id: '60d5ec49f1b2c8a1e8b4567a',
                  name: 'Samsung Galaxy S21'
                },
                message: {
                  smsId: '68e37285c83ac140f2485d20',
                  recipient: '+1234567890',
                  message: 'Hello from redgeGate!',
                  channel: 'sms',
                  status: 'delivered',
                  createdAt: '2024-01-15T10:30:00.000Z',
                  sentAt: '2024-01-15T10:30:10.000Z',
                  deliveredAt: '2024-01-15T10:30:25.000Z',
                  readAt: null,
                  updatedAt: '2024-01-15T10:30:25.000Z',
                  errorCode: null,
                  errorMessage: null
                }
              }
            }
          },
          {
            status: 404,
            description: 'Device or message not found',
            body: {
              success: false,
              error: {
                code: 'NOT_FOUND',
                message: 'The specified device ID or message ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/devices/60d5ec49f1b2c8a1e8b4567a/sms/msg_1a2b3c4d5e6f' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const deviceId = '60d5ec49f1b2c8a1e8b4567a';
const smsId = 'msg_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/v1/devices/\${deviceId}/sms/\${smsId}\`, {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.message);`,
          python: `import requests

device_id = '60d5ec49f1b2c8a1e8b4567a'
sms_id = '68e37285c83ac140f2485d20'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.get(f'https://text.teamredge.com/api/v1/devices/{device_id}/sms/{sms_id}', headers=headers)
data = response.json()
print(data['data']['message'])`
        }
      }
    ]
  },
  {
    name: 'Webhooks',
    icon: <Zap className="w-4 h-4" />,
    endpoints: [
      {
        method: 'POST',
        path: '/webhooks',
        summary: 'Create Webhook',
        description: 'Create a new webhook endpoint for event notifications',
        tags: ['Webhooks'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['url', 'events'],
            properties: {
              url: { type: 'string', format: 'uri' },
              events: {
                type: 'array',
                items: {
                  type: 'string',
                  enum: ['sms.sent', 'sms.delivered', 'sms.failed', 'sms.received', 'device.online', 'device.offline']
                }
              },
              secret: { type: 'string' }
            }
          },
          example: `{
  "url": "https://example.com/webhook",
  "events": ["sms.sent", "sms.delivered"],
  "secret": "your_webhook_secret"
}`
        },
        parameters: [],
        responses: [
          {
            status: 201,
            description: 'Webhook created successfully',
            body: {
              success: true,
              data: {
                id: 'webhook_1a2b3c4d5e6f',
                url: 'https://example.com/webhook',
                events: ['sms.sent', 'sms.delivered'],
                createdAt: '2024-01-15T10:30:00.000Z'
              }
            }
          }
        ]
      },
      {
        method: 'GET',
        path: '/webhooks',
        summary: 'List Webhooks',
        description: 'Retrieve all configured webhook endpoints for your account',
        tags: ['Webhooks'],
        parameters: [
          {
            name: 'page',
            type: 'integer',
            required: false,
            description: 'Page number for pagination',
            example: '1'
          },
          {
            name: 'limit',
            type: 'integer',
            required: false,
            description: 'Number of webhooks per page',
            example: '20'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Webhook list retrieved',
            body: {
              success: true,
              data: {
                webhooks: [
                  {
                    id: 'webhook_1a2b3c4d5e6f',
                    url: 'https://example.com/webhook',
                    events: ['sms.sent', 'sms.delivered'],
                    isActive: true,
                    deliveryCount: 150,
                    successCount: 142,
                    failureCount: 8,
                    lastDeliveryAt: '2024-01-15T10:25:00.000Z',
                    createdAt: '2024-01-10T10:30:00.000Z'
                  }
                ],
                pagination: {
                  page: 1,
                  limit: 20,
                  total: 5,
                  pages: 1,
                  hasNext: false,
                  hasPrev: false
                }
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/webhooks?page=1&limit=20' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/webhooks?page=1&limit=20', {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data.webhooks);`,
          python: `import requests

headers = {'X-API-Key': 'YOUR_API_KEY'}
params = {'page': 1, 'limit': 20}

response = requests.get('https://text.teamredge.com/api/webhooks', headers=headers, params=params)
data = response.json()
print(data['data']['webhooks'])`
        }
      },
      {
        method: 'DELETE',
        path: '/webhooks/:id',
        summary: 'Delete Webhook',
        description: 'Remove a webhook endpoint from your account',
        tags: ['Webhooks'],
        parameters: [
          {
            name: 'id',
            type: 'string',
            required: true,
            description: 'ID of the webhook to delete',
            example: 'webhook_1a2b3c4d5e6f'
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Webhook deleted successfully',
            body: {
              success: true,
              data: {
                message: 'Webhook deleted successfully',
                webhook: {
                  id: 'webhook_1a2b3c4d5e6f',
                  url: 'https://example.com/webhook',
                  deletedAt: '2024-01-15T10:30:00.000Z'
                }
              }
            }
          },
          {
            status: 404,
            description: 'Webhook not found',
            body: {
              success: false,
              error: {
                code: 'WEBHOOK_NOT_FOUND',
                message: 'The specified webhook ID was not found'
              }
            }
          }
        ],
        examples: {
          curl: `curl -X DELETE 'https://text.teamredge.com/api/webhooks/webhook_1a2b3c4d5e6f' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const webhookId = 'webhook_1a2b3c4d5e6f';
const response = await fetch(\`https://text.teamredge.com/api/webhooks/\${webhookId}\`, {
  method: 'DELETE',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data);`,
          python: `import requests

webhook_id = 'webhook_1a2b3c4d5e6f'
headers = {'X-API-Key': 'YOUR_API_KEY'}

response = requests.delete(f'https://text.teamredge.com/api/webhooks/{webhook_id}', headers=headers)
print(response.json())`
        }
      }
    ]
  },
  {
    name: 'Statistics',
    icon: <Settings className="w-4 h-4" />,
    endpoints: [
      {
        method: 'GET',
        path: '/stats',
        summary: 'Get Account Statistics',
        description: 'Retrieve usage statistics and analytics for your account',
        tags: ['Statistics'],
        parameters: [
          {
            name: 'period',
            type: 'string',
            required: false,
            description: 'Time period for statistics',
            example: '30d',
            schema: { enum: ['7d', '30d', '90d', '1y'] }
          }
        ],
        responses: [
          {
            status: 200,
            description: 'Statistics retrieved successfully',
            body: {
              success: true,
              data: {
                sms: {
                  totalSent: 1250,
                  totalDelivered: 1185,
                  totalFailed: 65,
                  deliveryRate: 94.8,
                  totalCost: { amount: 62.50, currency: 'USD' },
                  averageCost: { amount: 0.05, currency: 'USD' }
                },
                devices: {
                  total: 3,
                  online: 2,
                  offline: 1,
                  active: 2
                },
                webhooks: {
                  total: 2,
                  active: 2,
                  totalDeliveries: 850,
                  successRate: 96.5
                },
                usage: {
                  apiCalls: 1500,
                  bandwidthUsed: '2.4 MB',
                  storageUsed: '15.2 MB'
                },
                period: {
                  selected: '30d',
                  startDate: '2024-01-15T10:30:00.000Z',
                  endDate: '2024-02-14T10:30:00.000Z'
                }
              }
            }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/stats?period=30d' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/stats?period=30d', {
  method: 'GET',
  headers: {
    'X-API-Key': 'YOUR_API_KEY'
  }
});

const data = await response.json();
console.log(data.data);`,
          python: `import requests

headers = {'X-API-Key': 'YOUR_API_KEY'}
params = {'period': '30d'}

response = requests.get('https://text.teamredge.com/api/stats', headers=headers, params=params)
data = response.json()
print(data['data'])`
        }
      }
    ]
  },
  {
    name: 'WhatsApp',
    icon: <MessageCircle className="w-4 h-4 text-green-500" />,
    endpoints: [
      {
        method: 'POST',
        path: '/v1/whatsapp/send',
        summary: 'Send WhatsApp Message',
        description: 'Send a WhatsApp text message using your linked WhatsApp device. Uses the first available enabled WhatsApp device unless deviceId is specified. `to` accepts either a phone number with country code (e.g. 628123456789 for +62 812-3456-789) or a WhatsApp group ID ending in @g.us (see "List WhatsApp Groups" below to discover group IDs). Supports WhatsApp formatting: *bold*, _italic_, ~strikethrough~.',
        tags: ['WhatsApp'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['to', 'message'],
            properties: {
              to: { type: 'string', description: 'Recipient phone number with country code (no + or spaces), or a WhatsApp group ID ending in @g.us' },
              message: { type: 'string', maxLength: 4096, description: 'Message text. Supports *bold*, _italic_, ~strikethrough~' },
              deviceId: { type: 'string', description: 'Optional: specific WhatsApp device ID' }
            }
          },
          example: `{
  "to": "628123456789",
  "message": "Hello from *redgeGate* WhatsApp Gateway!",
  "deviceId": "60d5ec49f1b2c8a1e8b4567a"
}`
        },
        parameters: [],
        responses: [
          {
            status: 201,
            description: 'Message sent successfully. Poll GET /api/v1/messages/:id for delivery and read status updates.',
            body: {
              status: 'success',
              data: {
                id: '60d5ec49f1b2c8a1e8b4567b',
                recipient: '628123456789',
                message: 'Hello from *redgeGate* WhatsApp Gateway!',
                channel: 'whatsapp',
                status: 'sent',
                sentAt: '2026-06-11T08:00:00.000Z'
              }
            }
          },
          {
            status: 401,
            description: 'Invalid or missing API key',
            body: { error: 'API key required' }
          },
          {
            status: 404,
            description: 'No enabled WhatsApp device found',
            body: { status: 'error', message: 'No enabled WhatsApp device found' }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "to": "628123456789",
    "message": "Hello from *redgeGate* WhatsApp Gateway!"
  }'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '628123456789',
    message: 'Hello from *redgeGate* WhatsApp Gateway!'
  })
});

const data = await response.json();
console.log(data);`,
          python: `import requests

url = 'https://text.teamredge.com/api/v1/whatsapp/send'
headers = {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
}
payload = {
    'to': '628123456789',
    'message': 'Hello from *redgeGate* WhatsApp Gateway!'
}

response = requests.post(url, headers=headers, json=payload)
print(response.json())`
        }
      },
      {
        method: 'POST',
        path: '/v1/whatsapp/send-bulk',
        summary: 'Send Bulk WhatsApp',
        description: 'Send WhatsApp text messages to multiple recipients. Each message is sent sequentially with a typing indicator before send, a configurable delay between messages (set via Device Settings → Message Interval), and ±40% random jitter on each gap to mimic human behaviour and reduce ban risk.',
        tags: ['WhatsApp'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['messages'],
            properties: {
              deviceId: { type: 'string', description: 'Optional: specific WhatsApp device ID' },
              messages: {
                type: 'array',
                items: {
                  type: 'object',
                  required: ['to', 'message'],
                  properties: {
                    to: { type: 'string' },
                    message: { type: 'string' }
                  }
                }
              }
            }
          },
          example: `{
  "deviceId": "60d5ec49f1b2c8a1e8b4567a",
  "messages": [
    { "to": "628123456789", "message": "Hello John!" },
    { "to": "628987654321", "message": "Hello Jane!" }
  ]
}`
        },
        parameters: [],
        responses: [
          {
            status: 201,
            description: 'Messages queued for sending',
            body: {
              status: 'success',
              data: { queued: 2 }
            }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-bulk' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "messages": [
      { "to": "628123456789", "message": "Hello John!" },
      { "to": "628987654321", "message": "Hello Jane!" }
    ]
  }'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-bulk', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    messages: [
      { to: '628123456789', message: 'Hello John!' },
      { to: '628987654321', message: 'Hello Jane!' }
    ]
  })
});

const data = await response.json();
console.log(data);`,
          python: `import requests

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-bulk',
    headers={'X-API-Key': 'YOUR_API_KEY', 'Content-Type': 'application/json'},
    json={
        'messages': [
            {'to': '628123456789', 'message': 'Hello John!'},
            {'to': '628987654321', 'message': 'Hello Jane!'}
        ]
    }
)
print(response.json())`
        }
      },
      {
        method: 'POST',
        path: '/v1/whatsapp/send-media',
        summary: 'Send Media (Image/Document)',
        description: 'Send an image or document to a single recipient. Uses API key auth — no device ID in the URL. Before sending, the gateway shows a "recording/attaching" indicator (1–5s, scales with file size) to mimic a human uploading the file. Supported types: image (JPG, PNG, GIF, WebP) and document (PDF, etc.). Always include mimeType to ensure the correct file type is delivered.',
        tags: ['WhatsApp'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['to', 'type', 'data', 'mimeType'],
            properties: {
              to: { type: 'string', description: 'Recipient phone number with country code, or a WhatsApp group ID ending in @g.us' },
              type: { type: 'string', enum: ['image', 'document'], description: '"image" or "document"' },
              data: { type: 'string', description: 'Base64-encoded file content (no data URI prefix)' },
              mimeType: { type: 'string', description: 'MIME type (e.g. "application/pdf", "image/jpeg")' },
              filename: { type: 'string', description: 'Filename with extension — required for documents' },
              caption: { type: 'string', description: 'Optional caption shown below the media' },
              deviceId: { type: 'string', description: 'Optional: specific WhatsApp device ID' }
            }
          },
          example: `{
  "to": "628123456789",
  "type": "document",
  "data": "JVBERi0xLjQgMSAwIG9iao...",
  "mimeType": "application/pdf",
  "filename": "invoice.pdf",
  "caption": "See attached invoice"
}`
        },
        parameters: [],
        responses: [
          {
            status: 201,
            description: 'Media sent successfully',
            body: { status: 'success', data: { sent: true, type: 'document', to: '628123456789', id: '60d5ec49f1b2c8a1e8b4567b' } }
          },
          {
            status: 400,
            description: 'Missing required fields or invalid type',
            body: { status: 'error', message: 'to, type, data and mimeType are required' }
          },
          {
            status: 404,
            description: 'No enabled WhatsApp device found',
            body: { status: 'error', message: 'No enabled WhatsApp device found' }
          }
        ],
        examples: {
          curl: `# Encode your file to base64 first
B64=$(base64 -w 0 invoice.pdf)

curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-media' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d "{
    \\"to\\": \\"628123456789\\",
    \\"type\\": \\"document\\",
    \\"data\\": \\"$B64\\",
    \\"mimeType\\": \\"application/pdf\\",
    \\"filename\\": \\"invoice.pdf\\",
    \\"caption\\": \\"See attached invoice\\"
  }"`,
          javascript: `const file = document.querySelector('input[type=file]').files[0];
const base64 = await new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.readAsDataURL(file);
});

const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-media', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '628123456789',
    type: 'document',
    data: base64,
    mimeType: file.type,
    filename: file.name,
    caption: 'See attached invoice'
  })
});

const data = await response.json();
console.log(data);`,
          python: `import base64, requests

with open('invoice.pdf', 'rb') as f:
    b64 = base64.b64encode(f.read()).decode()

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-media',
    headers={'X-API-Key': 'YOUR_API_KEY', 'Content-Type': 'application/json'},
    json={
        'to': '628123456789',
        'type': 'document',
        'data': b64,
        'mimeType': 'application/pdf',
        'filename': 'invoice.pdf',
        'caption': 'See attached invoice'
    }
)
print(response.json())`
        }
      },
      {
        method: 'POST',
        path: '/v1/whatsapp/send-bulk-media',
        summary: 'Send Bulk Media (Image/Document)',
        description: 'Send the same image or document to multiple recipients via API key auth. Each recipient is processed sequentially: the gateway shows a "recording/attaching" indicator (scales with file size, 1–5s), sends the file, then waits a randomised interval (device Message Interval ±40%) before the next recipient. Each recipient can have a personalised caption.',
        tags: ['WhatsApp'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['recipients', 'media'],
            properties: {
              deviceId: { type: 'string', description: 'Optional: specific WhatsApp device ID' },
              recipients: {
                type: 'array',
                description: 'List of recipients, each with an optional personalised caption',
                items: {
                  type: 'object',
                  required: ['to'],
                  properties: {
                    to: { type: 'string', description: 'Recipient phone number with country code' },
                    caption: { type: 'string', description: 'Optional caption shown below the media' }
                  }
                }
              },
              media: {
                type: 'object',
                required: ['type', 'base64', 'mimetype'],
                properties: {
                  type: { type: 'string', enum: ['image', 'document'], description: '"image" or "document"' },
                  base64: { type: 'string', description: 'Base64-encoded file content (no data URI prefix)' },
                  mimetype: { type: 'string', description: 'MIME type (e.g. "image/jpeg", "application/pdf")' },
                  filename: { type: 'string', description: 'Filename with extension — required for documents' }
                }
              }
            }
          },
          example: `{
  "recipients": [
    { "to": "628123456789", "caption": "Hi John, see attached!" },
    { "to": "628987654321", "caption": "Hi Jane, see attached!" }
  ],
  "media": {
    "type": "image",
    "base64": "/9j/4AAQSkZJRgAB...",
    "mimetype": "image/jpeg"
  }
}`
        },
        parameters: [],
        responses: [
          {
            status: 201,
            description: 'Bulk media send completed. Returns per-recipient results including any failures.',
            body: {
              status: 'success',
              data: {
                total: 2,
                sent: 2,
                failed: 0,
                results: [
                  { to: '628123456789', id: '60d5ec49f1b2c8a1e8b4567b' },
                  { to: '628987654321', id: '60d5ec49f1b2c8a1e8b4567c' }
                ]
              }
            }
          },
          {
            status: 400,
            description: 'Missing required fields',
            body: { status: 'error', message: 'media.base64, media.mimetype and media.type are required' }
          },
          {
            status: 404,
            description: 'No enabled WhatsApp device found',
            body: { status: 'error', message: 'No enabled WhatsApp device found' }
          }
        ],
        examples: {
          curl: `# Encode your file first
B64=$(base64 -w 0 promo.jpg)

curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-bulk-media' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d "{
    \\"recipients\\": [
      { \\"to\\": \\"628123456789\\", \\"caption\\": \\"Hi John!\\" },
      { \\"to\\": \\"628987654321\\", \\"caption\\": \\"Hi Jane!\\" }
    ],
    \\"media\\": {
      \\"type\\": \\"image\\",
      \\"base64\\": \\"$B64\\",
      \\"mimetype\\": \\"image/jpeg\\"
    }
  }"`,
          javascript: `const file = document.querySelector('input[type=file]').files[0];
const base64 = await new Promise((resolve) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result.split(',')[1]);
  reader.readAsDataURL(file);
});

const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-bulk-media', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    recipients: [
      { to: '628123456789', caption: 'Hi John!' },
      { to: '628987654321', caption: 'Hi Jane!' }
    ],
    media: {
      type: 'image',
      base64: base64,
      mimetype: file.type
    }
  })
});

const data = await response.json();
console.log(data);`,
          python: `import base64, requests

with open('promo.jpg', 'rb') as f:
    b64 = base64.b64encode(f.read()).decode()

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-bulk-media',
    headers={'X-API-Key': 'YOUR_API_KEY', 'Content-Type': 'application/json'},
    json={
        'recipients': [
            {'to': '628123456789', 'caption': 'Hi John!'},
            {'to': '628987654321', 'caption': 'Hi Jane!'}
        ],
        'media': {
            'type': 'image',
            'base64': b64,
            'mimetype': 'image/jpeg'
        }
    }
)
print(response.json())`
        }
      },
      {
        method: 'GET',
        path: '/v1/whatsapp/groups',
        summary: 'List WhatsApp Groups',
        description: 'List the WhatsApp groups visible to your linked device (groups you are a member of). Use the returned `id` (ending in @g.us) as the `to` value on send/send-media/send-poll to message a group instead of an individual.',
        tags: ['WhatsApp'],
        parameters: [
          { name: 'deviceId', type: 'string', required: false, description: 'Optional: specific WhatsApp device ID (query param). Auto-selects first enabled WhatsApp device if omitted.' }
        ],
        responses: [
          {
            status: 200,
            description: 'Groups retrieved',
            body: {
              status: 'success',
              data: {
                groups: [
                  { id: '120363026841854347@g.us', name: 'Sales Team', participantsCount: 12, isAdmin: true },
                  { id: '120363028731868232@g.us', name: 'Support Channel', participantsCount: 34, isAdmin: false }
                ]
              }
            }
          },
          {
            status: 404,
            description: 'No enabled WhatsApp device found',
            body: { status: 'error', message: 'No enabled WhatsApp device found' }
          }
        ],
        examples: {
          curl: `curl -X GET 'https://text.teamredge.com/api/v1/whatsapp/groups' \\
  -H 'X-API-Key: YOUR_API_KEY'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/groups', {
  headers: { 'X-API-Key': 'YOUR_API_KEY' }
});

const data = await response.json();
console.log(data.data.groups);`,
          python: `import requests

response = requests.get(
    'https://text.teamredge.com/api/v1/whatsapp/groups',
    headers={'X-API-Key': 'YOUR_API_KEY'}
)
print(response.json()['data']['groups'])`
        }
      },
      {
        method: 'POST',
        path: '/v1/whatsapp/send-poll',
        summary: 'Send WhatsApp Poll',
        description: 'Send a WhatsApp poll to a phone number or a group. Uses the first available enabled WhatsApp device unless deviceId is specified. `to` accepts either a phone number with country code or a WhatsApp group ID ending in @g.us (see "List WhatsApp Groups").',
        tags: ['WhatsApp'],
        requestBody: {
          contentType: 'application/json',
          schema: {
            type: 'object',
            required: ['to', 'pollName', 'pollOptions'],
            properties: {
              to: { type: 'string', description: 'Recipient phone number with country code, or a WhatsApp group ID ending in @g.us' },
              pollName: { type: 'string', description: 'The poll question/title' },
              pollOptions: { type: 'array', items: { type: 'string' }, description: 'Poll answer options (minimum 2, maximum 12)' },
              allowMultipleAnswers: { type: 'boolean', description: 'Allow respondents to select more than one option. Defaults to false.' },
              deviceId: { type: 'string', description: 'Optional: specific WhatsApp device ID' }
            }
          },
          example: `{
  "to": "628123456789",
  "pollName": "How is the service?",
  "pollOptions": ["Excellent", "Good", "Average", "Poor"],
  "allowMultipleAnswers": false
}`
        },
        parameters: [],
        responses: [
          {
            status: 201,
            description: 'Poll sent successfully',
            body: {
              status: 'success',
              data: {
                id: '60d5ec49f1b2c8a1e8b4567b',
                recipient: '628123456789',
                pollName: 'How is the service?',
                pollOptions: ['Excellent', 'Good', 'Average', 'Poor'],
                status: 'sent',
                sentAt: '2026-07-08T08:00:00.000Z'
              }
            }
          },
          {
            status: 400,
            description: 'Missing required fields or fewer than 2 poll options',
            body: { status: 'error', message: 'pollName and at least 2 pollOptions are required' }
          },
          {
            status: 404,
            description: 'No enabled WhatsApp device found',
            body: { status: 'error', message: 'No enabled WhatsApp device found' }
          }
        ],
        examples: {
          curl: `curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-poll' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "to": "628123456789",
    "pollName": "How is the service?",
    "pollOptions": ["Excellent", "Good", "Average", "Poor"],
    "allowMultipleAnswers": false
  }'`,
          javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-poll', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '628123456789',
    pollName: 'How is the service?',
    pollOptions: ['Excellent', 'Good', 'Average', 'Poor'],
    allowMultipleAnswers: false
  })
});

const data = await response.json();
console.log(data);`,
          python: `import requests

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-poll',
    headers={'X-API-Key': 'YOUR_API_KEY', 'Content-Type': 'application/json'},
    json={
        'to': '628123456789',
        'pollName': 'How is the service?',
        'pollOptions': ['Excellent', 'Good', 'Average', 'Poor'],
        'allowMultipleAnswers': False
    }
)
print(response.json())`
        }
      },
    {
      method: 'POST',
      path: '/v1/whatsapp/send-location',
      summary: 'Send WhatsApp Location',
      tags: ['WhatsApp'],
      description: 'Send a location pin to a WhatsApp contact or group. The recipient will see a tappable map pin.',
      parameters: [
        { name: 'to', type: 'string', required: true, description: 'Recipient phone number with country code, or a WhatsApp group ID ending in @g.us', example: '628123456789' },
        { name: 'latitude', type: 'number', required: true, description: 'Latitude coordinate', example: '40.7128' },
        { name: 'longitude', type: 'number', required: true, description: 'Longitude coordinate', example: '-74.0060' },
        { name: 'description', type: 'string', required: false, description: 'Location description text', example: 'New York City' },
        { name: 'address', type: 'string', required: false, description: 'Street address displayed under the pin', example: '350 5th Ave, New York, NY 10118' },
        { name: 'deviceId', type: 'string', required: false, description: 'Specific WhatsApp device ID; auto-selects first enabled device if omitted' }
      ],
      requestBody: {
        contentType: 'application/json',
        schema: { type: 'object', required: ['to', 'latitude', 'longitude'], properties: { to: { type: 'string' }, latitude: { type: 'number' }, longitude: { type: 'number' }, description: { type: 'string' }, address: { type: 'string' }, deviceId: { type: 'string' } } },
        example: JSON.stringify({ to: '628123456789', latitude: 40.7128, longitude: -74.006, description: 'New York City', address: '350 5th Ave, New York, NY 10118' }, null, 2)
      },
      responses: [
        { status: 201, description: 'Location sent', example: JSON.stringify({ status: 'success', data: { id: '64f1a2b3c4d5e6f7a8b9c0d1', recipient: '628123456789', latitude: 40.7128, longitude: -74.006, status: 'sent', sentAt: '2024-01-15T10:30:00.000Z' } }, null, 2) },
        { status: 400, description: 'Missing latitude/longitude', example: JSON.stringify({ status: 'error', message: 'latitude and longitude are required' }, null, 2) }
      ],
      examples: {
        curl: `curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-location' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "to": "628123456789",
    "latitude": 40.7128,
    "longitude": -74.006,
    "description": "New York City",
    "address": "350 5th Ave, New York, NY 10118"
  }'`,
        javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-location', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '628123456789',
    latitude: 40.7128,
    longitude: -74.006,
    description: 'New York City',
    address: '350 5th Ave, New York, NY 10118'
  })
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-location',
    headers={'X-API-Key': 'YOUR_API_KEY'},
    json={
        'to': '628123456789',
        'latitude': 40.7128,
        'longitude': -74.006,
        'description': 'New York City',
        'address': '350 5th Ave, New York, NY 10118'
    }
)
print(response.json())`
      }
    },
    {
      method: 'POST',
      path: '/v1/whatsapp/send-catalog',
      summary: 'Send WhatsApp Catalog',
      tags: ['WhatsApp'],
      description: 'Send your WhatsApp Business catalog to a contact or group. The recipient sees a browseable product catalog card. Requires a WhatsApp Business account with catalog set up.',
      parameters: [
        { name: 'to', type: 'string', required: true, description: 'Recipient phone number with country code, or a WhatsApp group ID ending in @g.us', example: '628123456789' },
        { name: 'body', type: 'string', required: false, description: 'Optional text body message sent with the catalog', example: 'Check out our products!' },
        { name: 'deviceId', type: 'string', required: false, description: 'Specific WhatsApp device ID; auto-selects first enabled device if omitted' }
      ],
      requestBody: {
        contentType: 'application/json',
        schema: { type: 'object', required: ['to'], properties: { to: { type: 'string' }, body: { type: 'string' }, deviceId: { type: 'string' } } },
        example: JSON.stringify({ to: '628123456789', body: 'Check out our products!' }, null, 2)
      },
      responses: [
        { status: 201, description: 'Catalog sent', example: JSON.stringify({ status: 'success', data: { id: '64f1a2b3c4d5e6f7a8b9c0d1', recipient: '628123456789', status: 'sent', sentAt: '2024-01-15T10:30:00.000Z' } }, null, 2) },
        { status: 404, description: 'No enabled WhatsApp device found', example: JSON.stringify({ status: 'error', message: 'No enabled WhatsApp device found' }, null, 2) }
      ],
      examples: {
        curl: `curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-catalog' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "to": "628123456789",
    "body": "Check out our products!"
  }'`,
        javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-catalog', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '628123456789',
    body: 'Check out our products!'
  })
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-catalog',
    headers={'X-API-Key': 'YOUR_API_KEY'},
    json={
        'to': '628123456789',
        'body': 'Check out our products!'
    }
)
print(response.json())`
      }
    },
    {
      method: 'POST',
      path: '/v1/whatsapp/send-product',
      summary: 'Send WhatsApp Product',
      tags: ['WhatsApp'],
      description: 'Send a single product from your WhatsApp Business catalog to a contact or group. The recipient sees a product card with image, price, and description. Requires a WhatsApp Business account with catalog set up.',
      parameters: [
        { name: 'to', type: 'string', required: true, description: 'Recipient phone number with country code, or a WhatsApp group ID ending in @g.us', example: '628123456789' },
        { name: 'productId', type: 'string', required: true, description: 'Product ID from your WhatsApp Business catalog', example: 'product_12345' },
        { name: 'body', type: 'string', required: false, description: 'Optional text body message sent with the product', example: 'Check out this item!' },
        { name: 'deviceId', type: 'string', required: false, description: 'Specific WhatsApp device ID; auto-selects first enabled device if omitted' }
      ],
      requestBody: {
        contentType: 'application/json',
        schema: { type: 'object', required: ['to', 'productId'], properties: { to: { type: 'string' }, productId: { type: 'string' }, body: { type: 'string' }, deviceId: { type: 'string' } } },
        example: JSON.stringify({ to: '628123456789', productId: 'product_12345', body: 'Check out this item!' }, null, 2)
      },
      responses: [
        { status: 201, description: 'Product sent', example: JSON.stringify({ status: 'success', data: { id: '64f1a2b3c4d5e6f7a8b9c0d1', recipient: '628123456789', productId: 'product_12345', status: 'sent', sentAt: '2024-01-15T10:30:00.000Z' } }, null, 2) },
        { status: 400, description: 'Missing productId', example: JSON.stringify({ status: 'error', message: 'productId is required' }, null, 2) }
      ],
      examples: {
        curl: `curl -X POST 'https://text.teamredge.com/api/v1/whatsapp/send-product' \\
  -H 'X-API-Key: YOUR_API_KEY' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "to": "628123456789",
    "productId": "product_12345",
    "body": "Check out this item!"
  }'`,
        javascript: `const response = await fetch('https://text.teamredge.com/api/v1/whatsapp/send-product', {
  method: 'POST',
  headers: {
    'X-API-Key': 'YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    to: '628123456789',
    productId: 'product_12345',
    body: 'Check out this item!'
  })
});
const data = await response.json();`,
        python: `import requests

response = requests.post(
    'https://text.teamredge.com/api/v1/whatsapp/send-product',
    headers={'X-API-Key': 'YOUR_API_KEY'},
    json={
        'to': '628123456789',
        'productId': 'product_12345',
        'body': 'Check out this item!'
    }
)
print(response.json())`
      }
    }
  ]
  }
]

export default function StoplightStyleDocs() {
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('SMS')
  const [activeTab, setActiveTab] = useState<string>('request')
  const [selectedLanguage, setSelectedLanguage] = useState<string>('curl')
  const [darkMode, setDarkMode] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedCategory, setExpandedCategory] = useState<string>('SMS')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Try it out state
  const [tryItOutState, setTryItOutState] = useState<Record<string, TryItOutState>>(() => {
    const initialState: Record<string, TryItOutState> = {}
    apiCategories.forEach((category: ApiCategory) => {
      category.endpoints.forEach((endpoint: Endpoint) => {
        const key = `${endpoint.method}_${endpoint.path}`
        initialState[key] = {
          apiKey: '',
          parameters: {},
          requestBody: endpoint.requestBody?.example || '{}',
          isLoading: false,
          result: null,
          error: null,
          showCredentials: false
        }
      })
    })
    return initialState
  })

  // Static branding values
  const applicationName = 'msgGate'
  const tagline = 'Messaging Gateway Platform'

  const filteredCategories = apiCategories.map(category => ({
    ...category,
    endpoints: category.endpoints.filter(endpoint =>
      endpoint.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
      endpoint.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      endpoint.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    )
  })).filter(category => category.endpoints.length > 0)

  const syntaxStyle = darkMode ? oneDark : oneLight

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch (err) {
      console.error('Failed to copy: ', err)
      return false
    }
  }

  // Helper functions for Try it out
  const getTryItOutKey = (endpoint: Endpoint) => `${endpoint.method}_${endpoint.path}`

  const updateTryItOutState = (endpoint: Endpoint, updates: Partial<TryItOutState>) => {
    const key = getTryItOutKey(endpoint)
    setTryItOutState(prev => ({
      ...prev,
      [key]: { ...prev[key], ...updates }
    }))
  }

  const handleApiCall = async (endpoint: Endpoint) => {
    const key = getTryItOutKey(endpoint)
    const state = tryItOutState[key]

    if (!state.apiKey) {
      updateTryItOutState(endpoint, { error: 'API Key is required. Please enter your API key.' })
      return
    }

    updateTryItOutState(endpoint, { isLoading: true, error: null, result: null })

    try {
      const startTime = Date.now()
      const baseUrl = 'https://text.teamredge.com/api'
      let processedUrl = `${baseUrl}${endpoint.path}`

      // Replace path parameters (like {smsId}) with actual values
      Object.entries(state.parameters).forEach(([key, value]) => {
        if (value) {
          processedUrl = processedUrl.replace(`{${key}}`, value)
        }
      })

      // Build the request config
      const config: any = {
        method: endpoint.method,
        headers: {
          'X-API-Key': state.apiKey,
          'Content-Type': 'application/json'
        }
      }

      // Add remaining query parameters for GET requests (those not used in path)
      if (endpoint.method === 'GET' && Object.keys(state.parameters).length > 0) {
        const params = new URLSearchParams()

        // Check which parameters are path parameters by looking at the endpoint path
        const pathParameters = (endpoint.path.match(/\{([^}]+)\}/g) || []).map(param => param.slice(1, -1))

        Object.entries(state.parameters).forEach(([key, value]) => {
          // Only add as query parameter if it's not a path parameter and has a value
          if (value && !pathParameters.includes(key)) {
            params.append(key, value)
          }
        })
        if (params.toString()) {
          processedUrl += `?${params.toString()}`
        }
      }

      // Add request body for POST/PUT/PATCH requests
      if (['POST', 'PUT', 'PATCH'].includes(endpoint.method) && state.requestBody) {
        try {
          config.body = JSON.stringify(JSON.parse(state.requestBody))
        } catch (error) {
          throw new Error('Invalid JSON in request body')
        }
      }

      const response = await fetch(processedUrl, config)
      const duration = Date.now() - startTime

      // Get response headers
      const headers: Record<string, string> = {}
      response.headers.forEach((value, key) => {
        headers[key] = value
      })

      let data
      const contentType = response.headers.get('content-type')

      try {
        if (contentType?.includes('application/json')) {
          data = await response.json()
        } else {
          data = await response.text()
        }
      } catch (error) {
        data = null
      }

      const result: ApiTestResult = {
        status: response.status,
        statusText: response.statusText,
        headers,
        data,
        duration
      }

      updateTryItOutState(endpoint, { result, isLoading: false })
    } catch (error: any) {
      updateTryItOutState(endpoint, {
        error: error.message || 'Request failed',
        isLoading: false
      })
    }
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      {/* Header */}
      <header className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b sticky top-0 z-50`}>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className={`lg:hidden p-2 rounded-md mr-3 ${darkMode ? 'text-gray-400 hover:text-gray-100 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              <div className="h-10 w-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <MessageSquare className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{applicationName} API Reference</h1>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">{tagline}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'}`}
              >
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row">
        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 fixed lg:static
          w-80 h-screen lg:h-auto min-h-screen
          ${darkMode ? 'bg-gray-800' : 'bg-white'}
          border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'}
          overflow-y-auto z-50 lg:z-auto
          transition-transform duration-300 ease-in-out
          top-16 lg:top-0
        `}>
          <div className="p-4">
            <div className={`relative mb-4`}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search endpoints..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            <nav className="space-y-1">
              {filteredCategories.map((category) => (
                <div key={category.name}>
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category.name ? '' : category.name)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                      expandedCategory === category.name
                        ? darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-50 text-blue-900'
                        : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      {category.icon}
                      <span className="font-medium">{category.name}</span>
                    </div>
                    <span className="text-xs bg-gray-200 text-gray-700 rounded-full px-2 py-1">
                      {category.endpoints.length}
                    </span>
                  </button>

                  {expandedCategory === category.name && (
                    <div className="ml-6 mt-2 space-y-1">
                      {category.endpoints.map((endpoint) => (
                        <button
                          key={`${endpoint.method} ${endpoint.path}`}
                          onClick={() => {
                            setSelectedEndpoint(endpoint)
                            setSelectedCategory(category.name)
                          }}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            selectedEndpoint?.path === endpoint.path && selectedEndpoint?.method === endpoint.method
                              ? darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-50 text-blue-900'
                              : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                          }`}
                        >
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs font-semibold border rounded ${darkMode ? methodColorsDark[endpoint.method] : methodColors[endpoint.method]}`}>
                              {endpoint.method}
                            </span>
                            <span className="font-medium">{endpoint.summary}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-none lg:max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
            {!selectedEndpoint ? (
              <div className="text-center py-12">
                <Code className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h2 className="text-2xl font-bold mb-4">msgGate API Reference</h2>
                <p className={`mb-8 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Select an endpoint from the sidebar to view detailed documentation, examples, and test the API.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 text-left">
                  <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <Smartphone className="w-8 h-8 mb-3 text-blue-500" />
                    <h3 className="font-semibold mb-2">SMS Operations</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Send single and bulk SMS messages through your Android devices
                    </p>
                  </div>
                  <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <MessageCircle className="w-8 h-8 mb-3 text-green-500" />
                    <h3 className="font-semibold mb-2">WhatsApp Gateway</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Send WhatsApp messages and media with delivery & read receipts
                    </p>
                  </div>
                  <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <Database className="w-8 h-8 mb-3 text-purple-500" />
                    <h3 className="font-semibold mb-2">Device Management</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Monitor and manage your SMS and WhatsApp gateway devices
                    </p>
                  </div>
                  <div className={`p-6 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                    <Zap className="w-8 h-8 mb-3 text-amber-500" />
                    <h3 className="font-semibold mb-2">Webhooks & Events</h3>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      Receive real-time delivery, read, and inbound message events
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Header */}
                <div>
                  {/* Mobile breadcrumb */}
                  <nav className="flex items-center space-x-1 sm:space-x-2 text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 overflow-x-auto">
                    <button onClick={() => setSelectedEndpoint(null)} className="hover:text-blue-600 whitespace-nowrap">
                      API Reference
                    </button>
                    <span>/</span>
                    <span className="whitespace-nowrap">{selectedCategory}</span>
                    <span>/</span>
                    <span className="text-gray-900 truncate max-w-[120px] sm:max-w-none">{selectedEndpoint.summary}</span>
                  </nav>

                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 sm:space-x-3 mb-2">
                        <span className={`px-2 sm:px-4 py-2 text-xs sm:text-sm font-bold border rounded-lg ${darkMode ? methodColorsDark[selectedEndpoint.method] : methodColors[selectedEndpoint.method]}`}>
                          {selectedEndpoint.method}
                        </span>
                        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold leading-tight">{selectedEndpoint.summary}</h1>
                      </div>
                      <p className={`text-sm sm:text-base lg:text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {selectedEndpoint.description}
                      </p>
                    </div>
                    <button
                      className={`px-3 sm:px-4 py-2 rounded-lg font-medium text-sm sm:text-base ${darkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-700'} text-white whitespace-nowrap`}
                    >
                      <Play className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
                      Try it out
                    </button>
                  </div>
                </div>

                {/* Endpoint Path */}
                <div className={`p-3 sm:p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <code className={`font-mono text-xs sm:text-sm ${darkMode ? 'text-gray-100' : 'text-gray-900'} break-all sm:break-normal`}>
                    <span className={`inline-block px-2 py-1 text-xs font-bold border rounded mr-2 ${darkMode ? methodColorsDark[selectedEndpoint.method] : methodColors[selectedEndpoint.method]}`}>
                      {selectedEndpoint.method}
                    </span>
                    https://text.teamredge.com/api{selectedEndpoint.path}
                  </code>
                </div>

                {/* Tabs */}
                <div className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <nav className="flex overflow-x-auto space-x-6 sm:space-x-8 scrollbar-hide">
                    {['request', 'try-it-out', 'responses', 'examples'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                          activeTab === tab
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="mt-6">
                  {activeTab === 'request' && (
                    <div className="space-y-6">
                      {/* Parameters */}
                      {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Parameters</h3>
                          <div className={`rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'} overflow-x-auto`}>
                            <table className="w-full min-w-[600px]">
                              <thead className={`${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                                <tr>
                                  <th className="text-left p-4 font-medium">Name</th>
                                  <th className="text-left p-4 font-medium">Type</th>
                                  <th className="text-left p-4 font-medium">Required</th>
                                  <th className="text-left p-4 font-medium">Description</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedEndpoint.parameters.map((param, index) => (
                                  <tr key={param.name} className={`${index % 2 === 0 ? (darkMode ? 'bg-gray-800' : 'bg-gray-50') : ''}`}>
                                    <td className="p-4">
                                      <code className={`font-mono text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                        {param.name}
                                      </code>
                                    </td>
                                    <td className="p-4">
                                      <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {param.type}
                                      </span>
                                    </td>
                                    <td className="p-4">
                                      <span className={`text-xs px-2 py-1 rounded-full ${
                                        param.required
                                          ? darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                                          : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                                      }`}>
                                        {param.required ? 'Required' : 'Optional'}
                                      </span>
                                    </td>
                                    <td className="p-4">
                                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {param.description}
                                        {param.example && (
                                          <div className="mt-1">
                                            <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                              Example:
                                            </span>
                                            <code className={`ml-1 ${darkMode ? 'text-green-400' : 'text-green-600'}`}>
                                              {param.example}
                                            </code>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Request Body */}
                      {selectedEndpoint.requestBody && (
                        <div>
                          <h3 className="text-lg font-semibold mb-4">Request Body</h3>
                          <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                            <div className="mb-4">
                              <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                Content-Type:
                              </span>
                              <span className={`ml-2 font-mono text-sm ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                {selectedEndpoint.requestBody.contentType}
                              </span>
                            </div>
                            <div className="relative">
                              <SyntaxHighlighter
                                language="json"
                                style={syntaxStyle}
                                customStyle={{
                                  margin: 0,
                                  borderRadius: '0.5rem',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {selectedEndpoint.requestBody.example}
                              </SyntaxHighlighter>
                              <button
                                onClick={() => selectedEndpoint.requestBody?.example && copyToClipboard(selectedEndpoint.requestBody.example)}
                                className="absolute top-2 right-2 p-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'try-it-out' && selectedEndpoint && (() => {
                    const key = getTryItOutKey(selectedEndpoint)
                    const state = tryItOutState[key]
                    return (
                      <div className="space-y-6">
                        {/* Try it out Section */}
                        <div className="rounded-lg border border-gray-200 bg-white p-6">
                          <h3 className="text-lg font-semibold mb-6" style={{ fontSize: '18px', fontWeight: 600, lineHeight: '24px' }}>
                            Try it out
                          </h3>

                          {/* API Key Row */}
                          <div className="mb-4">
                            <label className="block text-sm font-medium mb-2" style={{ fontSize: '13px', fontWeight: 600, lineHeight: '18px', color: '#111827' }}>
                              API Key <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                              <input
                                type={state.showCredentials ? 'text' : 'password'}
                                value={state.apiKey}
                                onChange={(e) => updateTryItOutState(selectedEndpoint, { apiKey: e.target.value })}
                                className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                style={{
                                  background: '#FFFFFF',
                                  fontSize: '14px',
                                  paddingLeft: '36px'
                                }}
                              />
                              <Key className="absolute left-3 top-3 w-4 h-4 text-gray-400" style={{ color: '#6B7280' }} />
                              <button
                                onClick={() => updateTryItOutState(selectedEndpoint, { showCredentials: !state.showCredentials })}
                                className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                              >
                                {state.showCredentials ? (
                                  <EyeOff className="w-4 h-4" style={{ color: '#6B7280' }} />
                                ) : (
                                  <Eye className="w-4 h-4" style={{ color: '#6B7280' }} />
                                )}
                              </button>
                            </div>
                            <p className="text-xs mt-2" style={{ color: '#6B7280', fontSize: '12px', lineHeight: '16px' }}>
                              You can generate API keys from the dashboard
                            </p>
                          </div>

                          {/* Parameters Section */}
                          {selectedEndpoint.parameters && selectedEndpoint.parameters.length > 0 && (
                            <div className="mb-4 space-y-4">
                              {selectedEndpoint.parameters.map((param) => (
                                <div key={param.name}>
                                  <label className="block text-sm font-medium mb-2" style={{ fontSize: '13px', fontWeight: 600, lineHeight: '18px', color: '#111827' }}>
                                    {param.name}
                                    {param.required && <span className="text-red-500 ml-1">*</span>}
                                  </label>
                                  {param.description && (
                                    <p className="text-xs mb-2" style={{ color: '#6B7280', fontSize: '12px', lineHeight: '16px' }}>
                                      {param.description}
                                    </p>
                                  )}
                                  <input
                                    type="text"
                                    value={state.parameters[param.name] || ''}
                                    onChange={(e) => updateTryItOutState(selectedEndpoint, {
                                      parameters: { ...state.parameters, [param.name]: e.target.value }
                                    })}
                                    placeholder={param.example || `Enter ${param.name}`}
                                    className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    style={{
                                      background: '#FFFFFF',
                                      fontSize: '14px'
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Request Body Section */}
                          {selectedEndpoint.requestBody && (
                            <div className="mb-6">
                              <label className="block text-sm font-medium mb-2" style={{ fontSize: '13px', fontWeight: 600, lineHeight: '18px', color: '#111827' }}>
                                Request Body
                              </label>
                              <textarea
                                value={state.requestBody}
                                onChange={(e) => updateTryItOutState(selectedEndpoint, { requestBody: e.target.value })}
                                rows={6}
                                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                                style={{
                                  background: '#FFFFFF',
                                  fontSize: '14px'
                                }}
                                placeholder="Enter JSON request body..."
                              />
                            </div>
                          )}

                          {/* Send Request Button */}
                          <button
                            onClick={() => handleApiCall(selectedEndpoint)}
                            disabled={state.isLoading || !state.apiKey}
                            className="px-4 py-2.5 rounded-lg font-medium text-white flex items-center justify-center space-x-2 transition-colors disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                            style={{
                              background: state.isLoading || !state.apiKey ? '#D1D5DB' : '#2563EB',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.10)',
                              minWidth: '140px',
                              fontSize: '14px',
                              fontWeight: 500
                            }}
                          >
                            {state.isLoading ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin text-white" />
                                <span>Sending...</span>
                              </>
                            ) : (
                              <>
                                <Send className="w-4 h-4 text-white" />
                                <span>Send Request</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Response Section */}
                        {(state.result || state.error) && (
                          <div className="rounded-lg border border-gray-200 bg-white">
                            {state.error && (
                              <div className="p-6 text-red-600">
                                <div className="flex items-center space-x-2 mb-2">
                                  <AlertCircle className="w-5 h-5" />
                                  <span className="font-medium">Error</span>
                                </div>
                                <p className="text-sm">{state.error}</p>
                              </div>
                            )}

                            {state.result && (
                              <div className="p-6">
                                {/* Response Banner */}
                                <div className="flex items-center justify-between mb-4" style={{ height: '44px' }}>
                                  <div className="flex items-center space-x-3">
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      state.result.status >= 200 && state.result.status < 300
                                        ? 'bg-green-100 text-green-800 border border-green-300'
                                        : 'bg-red-100 text-red-800 border border-red-300'
                                    }`} style={{
                                      background: state.result.status >= 200 && state.result.status < 300 ? '#DEF7EC' : '#FEE2E2',
                                      color: state.result.status >= 200 && state.result.status < 300 ? '#03543F' : '#991B1B',
                                      borderColor: state.result.status >= 200 && state.result.status < 300 ? '#84E1BC' : '#FCA5A5',
                                      borderRadius: '999px',
                                      padding: '6px 10px',
                                      fontSize: '13px',
                                      fontWeight: 500
                                    }}>
                                      {state.result.status} {state.result.statusText}
                                    </span>
                                    <span className="text-sm" style={{ color: '#6B7280' }}>
                                      {state.result.duration}ms
                                    </span>
                                  </div>
                                  <button
                                    onClick={() => copyToClipboard(JSON.stringify(state.result?.data || {}, null, 2))}
                                    className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                                    style={{ color: '#6B7280' }}
                                    title="Copy response"
                                  >
                                    <Copy className="w-4 h-4" />
                                  </button>
                                </div>

                                {/* Response Code Box */}
                                <div className="rounded-lg border border-gray-300 overflow-hidden">
                                  <div className="relative">
                                    <SyntaxHighlighter
                                      language="json"
                                      style={{}}
                                      customStyle={{
                                        margin: 0,
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        background: '#FFFFFF',
                                        color: '#111827',
                                        padding: '16px',
                                        border: '1px solid #E5E7EB'
                                      }}
                                      codeTagProps={{
                                        style: {
                                          background: 'transparent',
                                          color: '#111827'
                                        }
                                      }}
                                    >
                                      {JSON.stringify(state.result.data, null, 2)}
                                    </SyntaxHighlighter>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })()}

                  {activeTab === 'responses' && (
                    <div className="space-y-6">
                      {selectedEndpoint.responses.map((response) => (
                        <div key={response.status}>
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-4">
                            <span className={`px-2 sm:px-3 py-1 rounded-lg font-medium text-sm sm:text-base inline-block w-fit ${
                              response.status >= 200 && response.status < 300
                                ? darkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800'
                                : response.status >= 400 && response.status < 500
                                ? darkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800'
                                : darkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800'
                            }`}>
                              {response.status}
                            </span>
                            <h3 className="text-base sm:text-lg font-semibold">{response.description}</h3>
                          </div>

                          {response.body && (
                            <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                              <div className="relative">
                                <SyntaxHighlighter
                                  language="json"
                                  style={syntaxStyle}
                                  customStyle={{
                                    margin: 0,
                                    borderRadius: '0.5rem',
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {JSON.stringify(response.body, null, 2)}
                                </SyntaxHighlighter>
                                <button
                                  onClick={() => copyToClipboard(JSON.stringify(response.body, null, 2))}
                                  className="absolute top-2 right-2 p-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {activeTab === 'examples' && selectedEndpoint.examples && (
                    <div className="space-y-6">
                      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4">
                        <label className="text-sm font-medium">Language:</label>
                        <select
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                          className={`px-3 py-2 rounded-lg border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'} w-full sm:w-auto`}
                        >
                          {Object.keys(selectedEndpoint.examples).map((lang) => (
                            <option key={lang} value={lang}>
                              {lang.charAt(0).toUpperCase() + lang.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                        <div className="relative">
                          <SyntaxHighlighter
                            language={selectedLanguage}
                            style={syntaxStyle}
                            customStyle={{
                              margin: 0,
                              borderRadius: '0.5rem',
                              fontSize: '0.875rem'
                            }}
                          >
                            {selectedEndpoint.examples[selectedLanguage]}
                          </SyntaxHighlighter>
                          <button
                            onClick={() => selectedEndpoint.examples?.[selectedLanguage] && copyToClipboard(selectedEndpoint.examples[selectedLanguage])}
                            className="absolute top-2 right-2 p-2 rounded bg-gray-700 hover:bg-gray-600 text-white"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}